<template>
    <Drawer @onClose="closeCallback">
        <!-- 总结信息区域 -->
        <div class="w-full p-4" v-if="summaryData">
            <div v-if="summaryData" class="flex w-full flex-col gap-2 rounded-lg bg-grey-10 p-[10px] text-sm">
                <div class="font-semibold">
                    {{ summaryData.title }}
                </div>

                <div class="border-b border-gray-40 pb-2 text-sm leading-6 text-gray-450">
                    {{ summaryData.year }}
                </div>

                <ul v-if="summaryData.bulletPoints && summaryData.bulletPoints.length" class="list-disc pl-4">
                    <li
                        v-for="(point, index) in summaryData.bulletPoints"
                        :key="index"
                        class="text-gray-450"
                    >
                        {{ point }}
                    </li>
                </ul>
            </div>
        </div>

        <div class="flex items-center justify-between p-4" v-if="displayTotal > 0">
            <p class="flex items-center gap-2 text-eureka-500">
                <span class="text-lg" v-show="!isLoading">{{ displayTotal }}</span>
                <span v-html="title" class="text-sm text-black"></span>
            </p>
        </div>

        <CatLoading v-if="isLoading" class="mx-auto mt-40" />

        <div v-else-if="isEmpty" class="mt-40 flex justify-center">
            <CatHole2 class="mb-2 h-[50px] w-[70px]" />
            <span class="text-sm text-grey-90">
                {{ t('noData') }}
            </span>
        </div>

        <!-- Direct sources mode -->
        <div v-else-if="sources" class="flex h-full flex-col gap-4 overflow-y-auto px-4 pb-4">
            <SolutionCard
                :order="index + 1"
                :solution="item"
                :track-data="trackData"
                v-for="(item, index) in sources"
                :key="item.SOLUTION_ID"
            />
        </div>

        <!-- API data mode -->
        <div v-else class="flex h-full flex-col gap-4 overflow-y-auto px-4 pb-4" ref="rightAside"
             @scroll="handleContentScroll">
            <SolutionCard
                :order="index + 1"
                :solution="item"
                :track-data="trackData"
                v-for="(item, index) in projectDrawerListSerIns.list"
                :key="item.SOLUTION_ID"
            />
            <div v-if="projectDrawerListSerIns.moreLoading" class="mx-auto">
                <span class="th-loading th-loading-spinner th-loading-md"></span>
            </div>
        </div>
    </Drawer>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import { SolutionCard } from '@eureka/shared/solution-card';
import type { IRightListComponentData } from '@/types';
import type { ISolution } from '@eureka/shared/solution-card/src/types';
import { Drawer } from '@eureka/shared/drawer'
import { CatLoading, CatHole2 } from '@eureka/cat'
import { ProjectDrawerListService } from '@/services/project-drawer-list-service';

import { useI18n } from 'vue-i18n';
const { t } = useI18n()

const props = defineProps<{
    title: string;
    closeCallback: () => void;
    data?: IRightListComponentData;
    sources?: ISolution[];
    summaryData?: {
        title: string;
        year: string;
        bulletPoints?: string[];
    };
    trackData?: Record<string, string>
}>()

const rightAside = ref(null)
const projectDrawerListSerIns = ref(new ProjectDrawerListService(rightAside as unknown as HTMLDivElement))

// 计算属性来处理两种模式
const isLoading = computed(() => {
    if (props.sources) return false;
    return projectDrawerListSerIns.value.loading;
});

const isEmpty = computed(() => {
    if (props.sources) return props.sources.length === 0;
    return projectDrawerListSerIns.value.isEmpty;
});

const displayTotal = computed(() => {
    if (props.sources) return props.sources.length;
    return Number(projectDrawerListSerIns.value.total) || 0;
});

onMounted(() => {
    // 只有在非 sources 模式下才初始化 API 数据加载
    if (!props.sources) {
        projectDrawerListSerIns.value.loadInitialData({
            ...props.data,
        })
    }
})

function handleContentScroll() {
    // 只有在非 sources 模式下才处理滚动加载
    if (!props.sources) {
        projectDrawerListSerIns.value.handleScroll({
            ...props.data,
        })
    }
}

</script>
